import { log } from "@/logger";
import {
    AuthenticationClient,
    type AuthenticationClientOptions,
} from "@feathersjs/authentication-client";
import type { Application, Params } from "@feathersjs/feathers";
import { AuthenticationRequest } from "@spork/feathers-client";
import { Platform } from "react-native";

import { getParentDeviceInfo } from "@/lib/deviceInfo";

/**
 * We use default Feathers Auth client, with some modifications.
 */
export class JwtAuthenticationClient extends AuthenticationClient {
    options: AuthenticationClientOptions;

    constructor(app: Application, options: AuthenticationClientOptions) {
        super(app, options);
        this.options = options;
    }

    // modified version from AuthenticationClient. The original method does trigger re-authentication on every disconnect.
    // We want to trigger re-authentication only when connection is disconnected and then reconnected.
    override handleSocket(socket: any) {
        // Connection events happen on every reconnect
        socket.on("disconnect", () => {
            log.debug("Socket disconnected.");
            if (this.authenticated) {
                log.debug(
                    "Authenticated socket disconnected. Setting up re-authentication on future re-connect."
                );
                const authPromise = new Promise<void>((resolve) =>
                    socket.once("connect", () => resolve())
                ).then(() => {
                    log.debug("Socket connected , trying to re-authenticate.");
                    return this.reAuthenticate(true).catch((error: Error) => {
                        log.debug(
                            "Reauthentication failed with %s.",
                            error.message
                        );
                    });
                });
                this.app.set("authentication", authPromise);
            }
        });
    }

    override async authenticate(
        authentication?: AuthenticationRequest,
        params?: Params
    ) {
        if (authentication) {
            const withDeviceInfo: AuthenticationRequest & {
                device_info?: ReturnType<typeof getParentDeviceInfo>;
                is_native_device?: boolean;
            } = {
                ...authentication,
                device_info: getParentDeviceInfo(),
            };
            // Native devices send is_native_device so the server issues a long-lived JWT (365 days).
            // Web clients omit the flag and get the default 12-hour token.
            if (Platform.OS !== "web") {
                withDeviceInfo.is_native_device = true;
            }
            authentication = withDeviceInfo;
        }
        // the socket.io connection is not opened by default - make sure it's opened before authenticating
        if (!this.app.io.connected) {
            log.debug("Socket is not connected. Opening.");
            this.app.io.open();
        }
        return super.authenticate(authentication, params);
    }
}